import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ProgressIndicator = ({ 
  sections, 
  currentSection, 
  currentQuestion, 
  totalQuestions, 
  completedQuestions,
  onSectionChange 
}) => {
  const overallProgress = (completedQuestions / totalQuestions) * 100;
  const currentSectionProgress = sections?.[currentSection] 
    ? (sections?.[currentSection]?.completed / sections?.[currentSection]?.total) * 100 
    : 0;

  return (
    <div className="bg-card border border-border rounded-lg p-4 shadow-elevation-1">
      {/* Overall Progress */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-foreground">
            Overall Progress
          </h3>
          <span className="text-sm text-muted-foreground">
            {completedQuestions} of {totalQuestions} questions
          </span>
        </div>
        <div className="w-full bg-muted rounded-full h-2">
          <div 
            className="bg-primary h-2 rounded-full nav-transition"
            style={{ width: `${overallProgress}%` }}
          />
        </div>
        <div className="flex items-center justify-between mt-1 text-xs text-muted-foreground">
          <span>0%</span>
          <span className="font-medium text-foreground">
            {Math.round(overallProgress)}%
          </span>
          <span>100%</span>
        </div>
      </div>
      {/* Current Section Progress */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-medium text-foreground">
            {sections?.[currentSection]?.name || 'Current Section'}
          </h4>
          <span className="text-sm text-muted-foreground">
            {sections?.[currentSection]?.completed || 0} of {sections?.[currentSection]?.total || 0}
          </span>
        </div>
        <div className="w-full bg-muted rounded-full h-1.5">
          <div 
            className="bg-accent h-1.5 rounded-full nav-transition"
            style={{ width: `${currentSectionProgress}%` }}
          />
        </div>
      </div>
      {/* Section Navigation */}
      <div className="space-y-2">
        <h4 className="text-sm font-medium text-foreground mb-2">Sections</h4>
        {sections?.map((section, index) => {
          const isActive = index === currentSection;
          const isCompleted = section?.completed === section?.total;
          const isAccessible = index <= currentSection || isCompleted;
          
          return (
            <button
              key={section?.id}
              onClick={() => isAccessible && onSectionChange(index)}
              disabled={!isAccessible}
              className={`w-full flex items-center space-x-3 p-2 rounded-md text-left nav-transition ${
                isActive
                  ? 'bg-primary text-primary-foreground'
                  : isCompleted
                  ? 'bg-success/10 text-success hover:bg-success/20'
                  : isAccessible
                  ? 'hover:bg-muted text-foreground'
                  : 'text-muted-foreground cursor-not-allowed opacity-50'
              }`}
            >
              <div className="flex-shrink-0">
                {isCompleted ? (
                  <Icon name="CheckCircle2" size={16} className="text-success" />
                ) : isActive ? (
                  <Icon name="Circle" size={16} className="text-primary-foreground" />
                ) : (
                  <Icon name="Circle" size={16} className="text-muted-foreground" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">
                  {section?.name}
                </div>
                <div className={`text-xs truncate ${
                  isActive ? 'text-primary-foreground/80' : 'text-muted-foreground'
                }`}>
                  {section?.description}
                </div>
              </div>
              <div className="flex-shrink-0 text-xs">
                {section?.completed}/{section?.total}
              </div>
            </button>
          );
        })}
      </div>
      {/* Quick Actions */}
      <div className="mt-4 pt-4 border-t border-border">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            iconName="Save"
            iconPosition="left"
          >
            Save Progress
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            iconName="RotateCcw"
            iconPosition="left"
          >
            Reset Section
          </Button>
        </div>
      </div>
      {/* Time Estimate */}
      <div className="mt-3 p-2 bg-muted rounded-md">
        <div className="flex items-center space-x-2">
          <Icon name="Clock" size={14} className="text-muted-foreground" />
          <span className="text-xs text-muted-foreground">
            Estimated time remaining: {Math.max(1, Math.ceil((totalQuestions - completedQuestions) * 2))} minutes
          </span>
        </div>
      </div>
    </div>
  );
};

export default ProgressIndicator;